<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { usePhoneCsvStore } from '../../../stores';
import { closeModal, updateShow } from '../../../app/components/modal/modals';
import type { ModalProps } from '../../../app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA } from './dnc-delete.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';
import { showAutoDismissToast, showErrorToast } from '@/noto';
import { useI18N } from '@dialpad/i18n';

const log = logger.getChild('DncDelete');
log.debug('Mounting');

const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  void closeModal();
  throw new Error('Missing DNC ID');
}

const { $t } = useI18N();

const phoneCsvStore = usePhoneCsvStore();

const dncCsv = phoneCsvStore.watch(() => props.meta?.id ?? '', 'DncDelete');
const mounted = ref(false);
const isDeleting = ref(false);

watch(
  () => dncCsv.value.error,
  (error) => {
    if (error) {
      log.log('Error fetching DNC CSV', error);
      void showErrorToast({
        content: {
          title: $t('DNC_FETCH_ERROR_TITLE'),
        },
      });
      void closeModal();
    }
  },
);

watch(
  [() => dncCsv.value.item, () => dncCsv.value.loading],
  ([dncCsvToDelete, loading]) => {
    if (!loading && !dncCsvToDelete) {
      log.log('DNC CSV was deleted');
      void showAutoDismissToast({
        content: {
          title: $t('DNC_DELETE_SUCCESS_TITLE'),
        },
      });
      void closeModal();
    }
  },
);

const confirm = async () => {
  log.debug('Confirm');
  if (!dncCsv.value.item?.id) {
    log.error('DNC CSV ID is missing');
    return;
  }

  isDeleting.value = true;
  log.debug('Deleting DNC CSV', dncCsv.value.item.id);
  phoneCsvStore.discard(dncCsv.value.item.id, {
    onSuccess: () => {
      isDeleting.value = false;
      void closeModal();
      void showAutoDismissToast({
        content: {
          title: $t('DNC_DELETE_SUCCESS_TITLE'),
        },
      });
    },
    onRetry: () => {
      isDeleting.value = false;
    },
    onError: (error) => {
      isDeleting.value = false;
      log.log('Error deleting DNC CSV', error);
      void showErrorToast({
        content: {
          title: $t('DNC_DELETE_ERROR_TITLE'),
        },
      });
    },
  });
};

const cancel = () => {
  log.debug('Cancel');
  void closeModal();
};

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;
});
const show = computed(() => mounted.value && !!dncCsv.value.item);
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('DNC_DELETE_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    {{ $t('DNC_DELETE_DESCRIPTION', { name: dncCsv.item?.name }) }}
    <footer class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('DNC_DELETE_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        kind="danger"
        importance="primary"
        class="d-ml6"
        :loading="isDeleting"
        @click="confirm"
      >
        {{ $t('DNC_DELETE_CONFIRM') }}
      </DtButton>
    </footer>
  </DtModal>
</template>
